/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import PropTypes from "prop-types";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

function PickupRawmatPrint({ parent, size }) {
  const mod = "quotation";
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [company, setCompany] = useState({});
  const [attr, setAttr] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const slipRef = useRef(null);
  const limitRow = 10;

  const handlePrint = useReactToPrint({
    content: () => slipRef.current,
  });

  const print = () => {
    setLoading(true);
    setCompany({});
    setAttr({});
    setList([]);

    FetchData("OPTIONS", `/${mod}/print`, app.token, {
      id: parent.id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setCompany(result.company);
        setAttr(result.data);
        setList(result.list);

        setLoading(false);

        handlePrint();
      }
    });
  };

  const renderHeader = () => {
    return (
      <div>
        <table width="100%" cellPadding={5}>
          <tbody>
            <tr>
              <td align="center">
                <h3>{company.name}</h3>
              </td>
            </tr>
            <tr>
              <td align="center" width="85%">
                {company.address} {company.branch}
              </td>
            </tr>
            <tr>
              <td align="center">เลขประจำตัวผู้เสียภาษี : {company.tax_id}</td>
            </tr>
            <tr>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </table>
        <table width="100%" cellPadding={5}>
          <tbody>
            <tr>
              <td align="center" colSpan={2}>
                <h3>ใบเสนอราคา</h3>
              </td>
            </tr>
            <tr>
              <td width="50%">ลูกค้า : {attr.customer_name}</td>
              <td align="right">NO. : {attr.code}</td>
            </tr>
            <tr>
              <td>เลขประจำตัวผู้เสียภาษีอากร : {attr.customer_taxid}</td>
              <td align="right">วันที่ : {Func.dateDisplay(attr.date_quotation)}</td>
            </tr>
            <tr>
              <td>เบอร์โทรศัพท์ : {attr.customer_tel === "" ? "-" : attr.customer_tel}</td>
              <td>อีเมล : {attr.customer_email === "" ? "-" : attr.customer_email}</td>
            </tr>
            <tr>
              <td colSpan={2}>ที่อยู่ : {attr.customer_address}</td>
            </tr>
          </tbody>
        </table>
        <table width="100%" cellPadding={5}>
          <tbody>
            <tr>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderOrder = () => {
    return (
      <table className="table" width="100%" cellPadding={5} cellSpacing={4} border="1">
        <thead>
          <tr>
            <th width="10%">ลำดับที่</th>
            <th>รายการ</th>
            <th width="10%">วันผลิต</th>
            <th width="15%">จำนวน</th>
            <th width="15%">หน่วยละ</th>
            <th width="15%">จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, i) => (
            <tr key={item.id}>
              <td align="center" valign="top">
                {i + 1}
              </td>
              <td valign="top">{item.name}</td>
              <td align="right" valign="top">
                {Func.numberDisplay(item.job_day, 0)} วัน
              </td>
              <td align="right" valign="top">
                {Func.numberDisplay(item.quantity)} {item.unit_name}
              </td>
              <td align="right" valign="top">
                {Func.numberDisplay(item.price)}
              </td>
              <td align="right" valign="top">
                {Func.numberDisplay(item.total)}
              </td>
            </tr>
          ))}
          {list.length < limitRow
            ? Array.from(Array(limitRow - list.length)).map((_, index) => (
                <tr key={index.toString()}>
                  <td align="center">{list.length + index + 1}</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))
            : null}
        </tbody>
        <tbody>
          <tr>
            <td colSpan={4} rowSpan={3} valign="middle" align="center">
              <div className="pricetext">({attr.pricetext})</div>
            </td>
            <th align="right">{t(`attr.amount`)}</th>
            <td align="right">{Func.numberDisplay(attr.total)}</td>
          </tr>
          <tr>
            <th align="right">{t(`attr.vat`)}</th>
            <td align="right">{Func.numberDisplay(attr.vat)}</td>
          </tr>
          <tr>
            <th align="right">{t(`attr.nettotal`)}</th>
            <td align="right">{Func.numberDisplay(attr.nettotal)}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderFooter = () => {
    return (
      <table width="100%" colSpan={5}>
        <tbody>
          <tr>
            <td colSpan={2}>
              <br />
              <br />
              <br />
            </td>
          </tr>
          <tr>
            <td align="center" width="50%">
              ผู้รับสินค้า ....................................................
            </td>
            <td align="center">ผู้รับเงิน ....................................................</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: "none" }}>
        <div ref={slipRef} className="print">
          <div>
            <div className="a4">
              {renderHeader()}
              {renderOrder()}
              {renderFooter()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return parent.status === "reject" ? null : (
    <div>
      {size === "small" ? (
        <MDButton variant="text" color="info" size="small" iconOnly onClick={print}>
          <Icon>print</Icon> {t(`element.print`)}
        </MDButton>
      ) : (
        <MDButton variant="gradient" color="info" onClick={print}>
          <Icon>print</Icon>&nbsp;{t(`element.print`)}
        </MDButton>
      )}
      {renderLoading()}
      {renderPrint()}
    </div>
  );
}

PickupRawmatPrint.defaultProps = {
  size: "small",
};

PickupRawmatPrint.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  size: PropTypes.string,
};

export default PickupRawmatPrint;
